import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import { cn } from '../../utils/cn';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';

interface TestimonialCarouselProps {
  testimonials: Array<{
    quote: string;
    author: string;
    role: string;
    image: string;
  }>;
  delay?: number;
}

const TestimonialCarousel: React.FC<TestimonialCarouselProps> = ({
  testimonials,
  delay = 0
}) => {
  return (
    <Swiper
      modules={[Pagination, Autoplay]}
      spaceBetween={30}
      slidesPerView={1}
      breakpoints={{
        640: {
          slidesPerView: 2,
        },
        1024: {
          slidesPerView: 3,
        },
      }}
      pagination={{
        clickable: true,
        bulletActiveClass: 'swiper-pagination-bullet-active !bg-blue-500',
      }}
      autoplay={{
        delay: 5000,
        disableOnInteraction: false,
      }}
      className="pb-12"
    >
      {testimonials.map((testimonial, index) => (
        <SwiperSlide key={index}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ 
              opacity: 1, 
              y: 0,
              transition: {
                duration: 0.5,
                delay: delay + (index * 0.1),
                ease: [0.22, 1, 0.36, 1]
              }
            }}
            whileHover={{ 
              scale: 1.05,
              transition: { duration: 0.2 }
            }}
            viewport={{ once: true }}
            className={cn(
              "bg-white dark:bg-gray-800 rounded-xl p-8 h-full",
              "shadow-lg border border-gray-100 dark:border-gray-700",
              "hover:shadow-xl transition-all duration-300",
              "hover:border-blue-500 dark:hover:border-blue-400",
              "group"
            )}
          >
            <div className="flex items-center mb-6">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className="h-5 w-5 text-yellow-400 fill-current transform group-hover:scale-110 transition-transform"
                />
              ))}
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-6 relative">
              <span className="text-5xl text-blue-500/20 absolute -top-4 -left-2">"</span>
              {testimonial.quote}
              <span className="text-5xl text-blue-500/20 absolute -bottom-8 -right-2">"</span>
            </p>
            <div className="flex items-center">
              <div className="relative">
                <img
                  src={testimonial.image}
                  alt={testimonial.author}
                  className="h-12 w-12 rounded-full object-cover transform group-hover:scale-110 transition-transform"
                  loading="lazy"
                />
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ 
                    scale: 1,
                    transition: {
                      duration: 0.5,
                      delay: delay + 0.3,
                      ease: [0.22, 1, 0.36, 1]
                    }
                  }}
                  viewport={{ once: true }}
                  className="absolute -bottom-1 -right-1 h-4 w-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-800"
                />
              </div>
              <div className="ml-4">
                <p className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {testimonial.author}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {testimonial.role}
                </p>
              </div>
            </div>
          </motion.div>
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

export default TestimonialCarousel;